import { useRouter } from 'next/router';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import MapGL, { Source, Layer as MapLayer, NavigationControl, Popup } from 'react-map-gl';
import type { MapRef } from 'react-map-gl';
import URI from 'urijs';
import { getCmtaLinks } from '@clubmed/domains-service';
import { windowWrapper } from '@clubmed/utils';
import { Button } from 'Components/frameworks/button/Button';
import Image from 'Components/frameworks/image/Image';
import { Layer } from 'Components/frameworks/layer/Layer';
import { Spinner } from 'Components/frameworks/spinner/Spinner';
import { ICONS } from 'Components/utils/icon/icons';
import { useResponsive } from 'Components/utils/responsive/ResponsiveContext';
import { ProductCard } from 'Components/widgets/product/product-card/ProductCard';
import config from 'Config';
import { ErrorBoundary } from 'Context/ErrorBoundary';
import { useAppContext } from 'Context/ReactAppContext';
import { t } from 'Src/i18n';
import { clearAll, clearFacets } from 'Store/faceting/facetingSearch.action';
import { MAP_STYLE_VALUE } from 'Store/search/world-map/worldMap.actions';
import { useTracking } from 'Tracking/tracking-api/useTracking';
import { getQueryParameters } from 'Utils/uri/uriUtils';
import { dataLocationList } from './dataLocationList';
import { clusterLayer, clusterCountLayer, unclusteredPointLayer } from './layers';
import { WorldMapFilterList } from './WorldMapFilterList';
const MAPBOX_TOKEN = config.get('pickYourRoom.mapbox.token');
const MAPBOX_HOST = config.get('pickYourRoom.mapbox.host');
const DEFAULT_LONGITUDE = 3;
const DEFAULT_LATITUDE = 36;
const DEFAULT_ZOOM = 1.7;
const DEFAULT_MAX_ZOOM = 14.5;
type Props = {
  offer?: Offer;
  onlyUniqResortProductList?: Array<any>;
};
export const SearchWorldMap = (props: Props) => {
  const appContext = useAppContext();
  const dispatch = useDispatch();
  const { isDesktop } = useResponsive();
  const router = useRouter();
  const isLoading = useSelector(({ faceting }) => faceting.isLoading);
  const [showLayer, setShowLayer] = useState(false);
  const [popupInfo, setPopupInfo] = useState(null);
  const [tooltipInfo, setTooltipInfo] = useState(null);
  const [cursor, setCursor] = useState<string>('auto');
  const [disableRotateOnMobile, setDisableRotateOnMobile] = useState(false);
  const [featureMapId, setFeatureMapId] = useState();
  const [isRenderWorldCopies, setIsRenderWorldCopies] = useState(true);
  const [maxZoom, setMaxZoom] = useState(null);
  const mapRef = useRef<MapRef>(null);
  const trackingData = {
    actionGA: 'worldMap',
    categoryGA: 'search',
  };
  const track = useTracking(trackingData);
  const { onlyUniqResortProductList, offer } = props;
  const defaultMapValue = {
    center: [DEFAULT_LONGITUDE, DEFAULT_LATITUDE],
    speed: 0.7,
    zoom: DEFAULT_ZOOM,
    essential: true,
  };
  let geojson = {
    type: 'FeatureCollection',
    properties: {
      title: 'Mapbox Club Med',
      'marker-symbol': 'monument',
    },
    crs: {
      type: 'name',
      properties: {
        name: 'urn:ogc:def:crs:OGC:1.3:CRS84',
      },
    },
  };
  const features = [];
  onlyUniqResortProductList?.forEach((resort) => {
    const resortCoordinates = {
      type: 'Feature',
      properties: resort,
      geometry: {
        type: 'Point',
        coordinates: [resort.coordinates?.longitude, resort.coordinates?.latitude],
      },
    };
    features.push(resortCoordinates);
  });
  geojson = { ...geojson, features } as typeof geojson & { features: unknown[] }; // TODO(TS-ZERO): tighten

  const handleFullScreen = (isShowLayer: boolean): void => {
    setShowLayer(isShowLayer);
    const searchPageUrl = isShowLayer
      ? new URI(router.asPath).clone().setQuery({
          withWorldMap: true,
        })
      : new URI(router.asPath).clone().removeQuery('withWorldMap');
    router.replace(searchPageUrl.toString(), undefined, {
      shallow: true,
    });
    setTooltipInfo(null);
  };

  const setDataLocation = (flyTo, lng, lat, pointCount) => {
    if (pointCount > 15) {
      return {
        ...flyTo,
        center: [dataLocationList[0].longitude, dataLocationList[0].latitude],
        zoom: dataLocationList[0].zoom,
      };
    }

    for (let i = 0; i < dataLocationList.length; i++) {
      if (
        lng < dataLocationList[i].longitudeHigh &&
        lng > dataLocationList[i].longitudeLow &&
        lat < dataLocationList[i].latitudeHigh &&
        lat > dataLocationList[i].latitudeLow
      ) {
        if (dataLocationList[i].region === 'ALPES' && pointCount <= 3) {
          return {
            ...flyTo,
            center: [dataLocationList[i].longitude, dataLocationList[i].latitude],
            zoom: 10,
          };
        }

        return {
          ...flyTo,
          center: [dataLocationList[i].longitude, dataLocationList[i].latitude],
          zoom: dataLocationList[i].zoom,
        };
      }
    }

    return flyTo;
  };

  const onClick = (event) => {
    try {
      if (event.features.length) {
        event.originalEvent.stopPropagation();
        let flyTo = {
          center: [event.lngLat.lng, event.lngLat.lat],
          speed: 0.7,
          zoom: 5,
          essential: true,
        };

        if (event.features[0].layer.id === 'cluster-count') {
          flyTo = setDataLocation(
            flyTo,
            event.lngLat.lng,
            event.lngLat.lat,
            event.features[0].properties.point_count,
          );
        } else {
          const {
            properties: {
              childcare,
              childcareAgeInMonths,
              comparison,
              coordinates,
              flag,
              header,
              kids,
              minAuthorizedAge,
              resortRegion,
              thumbnail,
              tripadvisor,
              ...rest
            },
          } = [...event.features][0];
          const product = {
            ...rest,
            childcare: JSON.parse(childcare),
            childcareAgeInMonths: (childcareAgeInMonths && JSON.parse(childcareAgeInMonths)) || [],
            comparison: JSON.parse(comparison),
            coordinates: JSON.parse(coordinates),
            flag: JSON.parse(flag),
            header: JSON.parse(header),
            kids: JSON.parse(kids),
            minAuthorizedAge: JSON.parse(minAuthorizedAge),
            thumbnail: JSON.parse(thumbnail),
          };
          track({
            labelGA: `clickTrident_${product.id}`,
            resortCountry: product.header.heading.subtitle,
            resortRegion,
          });
          setPopupInfo(product);
          flyTo = {
            center: [product.coordinates.longitude, product.coordinates.latitude],
            speed: 0.6,
            zoom: 5,
            essential: true,
          };
          setIsRenderWorldCopies(true);
        }

        mapRef.current?.flyTo(flyTo as Parameters<NonNullable<MapRef>['flyTo']>[0]);
      }
    } catch (error) {
      setPopupInfo(null);
    }
  };

  // Load the page in full screen or not
  useEffect(() => {
    const currentLocation = windowWrapper.getLocation();
    setShowLayer(getQueryParameters(currentLocation).withWorldMap);
    return () => {
      setMaxZoom(DEFAULT_MAX_ZOOM);
    };
  }, []);

  const setHover = (featureId, isHover) => {
    mapRef.current?.setFeatureState(
      {
        source: 'search-world-map',
        id: featureId,
      },
      {
        hover: isHover,
      },
    );
  };

  // Hover pin alone
  const onMouseEnter = useCallback(
    (event) => {
      try {
        setCursor('pointer');

        if (event.features.length && event.features[0].layer.id === 'unclustered-point') {
          const {
            properties: { coordinates, header, id },
          } = [...event.features][0];
          const product = {
            id,
            coordinates: JSON.parse(coordinates),
            header: JSON.parse(header),
          };
          setTooltipInfo(product);

          if (featureMapId) {
            setHover(event.features[0].id, false);
          }

          setFeatureMapId(id);
          setHover(event.features[0].id, true);
        }
      } catch (error) {
        setTooltipInfo(null);
      }
    },
    [tooltipInfo?.id],
  );

  const onMouseLeave = (event) => {
    if (event.features.length) {
      if (featureMapId) {
        setHover(event.features[0].id, false);
      }
    }

    setFeatureMapId(null);
    setTooltipInfo(null);
    setCursor('auto');
  };

  // Disable rotate on mobile
  const onRotateStart = () => {
    setDisableRotateOnMobile(true);
  };

  // Keep zoom only on mobile and disable rotate
  const onRotateEnd = () => {
    setDisableRotateOnMobile(false);
  };

  // Disable scroll horizontal
  const onDragStart = () => {
    setIsRenderWorldCopies(false);
    setPopupInfo(null);
  };

  // Active scroll horizontal to center the product card
  const onDragEnd = () => {
    setIsRenderWorldCopies(true);
  };

  // Set default value on map when filtered
  const setDefaultMapValue = () => {
    mapRef.current?.flyTo(defaultMapValue as Parameters<NonNullable<MapRef>['flyTo']>[0]);
  };

  const mapComponent = (
    <MapGL
      cursor={cursor}
      dragRotate={!disableRotateOnMobile}
      initialViewState={{
        latitude: DEFAULT_LATITUDE,
        longitude: DEFAULT_LONGITUDE,
        zoom: DEFAULT_ZOOM,
      }}
      interactiveLayerIds={[clusterCountLayer.id, unclusteredPointLayer.id]}
      mapboxAccessToken={MAPBOX_TOKEN as string}
      mapStyle={MAP_STYLE_VALUE}
      minZoom={DEFAULT_ZOOM}
      maxZoom={maxZoom}
      onClick={onClick}
      onDragEnd={onDragEnd}
      onDragStart={onDragStart}
      onMouseEnter={isDesktop ? onMouseEnter : null}
      onMouseLeave={isDesktop ? onMouseLeave : null}
      onRotateEnd={onRotateEnd}
      onRotateStart={onRotateStart}
      ref={mapRef}
      renderWorldCopies={isRenderWorldCopies}
      reuseMaps
      style={{
        width: '100vw',
        height: '100%',
      }}
      touchZoomRotate={!disableRotateOnMobile}
      transformRequest={(url) => {
        const uri = new URI(url).removeQuery('sku');
        const mapboxHost = uri.protocol() + '://' + uri.hostname();
        return {
          url: uri.toString().replace(mapboxHost, MAPBOX_HOST),
        };
      }}
    >
      <Source
        id="search-world-map"
        type="geojson"
        data={geojson as unknown as GeoJSON.FeatureCollection}
        cluster
        clusterMaxZoom={14}
        clusterRadius={50}
        generateId
      >
        {isDesktop && <NavigationControl position={'bottom-right' as const} showCompass={false} />}
        <MapLayer {...clusterLayer} />
        <MapLayer {...clusterCountLayer} />
        <MapLayer {...unclusteredPointLayer} />
      </Source>
      {tooltipInfo && isDesktop && (
        <Popup
          key={tooltipInfo.id}
          longitude={Number(tooltipInfo.coordinates.longitude)}
          latitude={Number(tooltipInfo.coordinates.latitude)}
          closeButton={false}
          closeOnClick
        >
          {tooltipInfo.header.heading.title}
        </Popup>
      )}
      {popupInfo && (
        <Popup
          anchor="top"
          key={popupInfo.id}
          longitude={Number(popupInfo.coordinates.longitude)}
          latitude={Number(popupInfo.coordinates.latitude)}
          closeButton={false}
          closeOnClick
          onClose={() => {
            setPopupInfo(null);
            setTooltipInfo(null);
          }}
          className="SearchWorldMap-productCard"
          maxWidth="280px"
          offset={-200}
        >
          <ProductCard
            cmtaLinks={getCmtaLinks(appContext.locale)}
            index={popupInfo.id}
            offer={offer}
            {...popupInfo}
          />
        </Popup>
      )}
    </MapGL>
  );
  const mapBackgroundImage = (
    <Image
      altMsg="map-picture"
      dontChangeAnything
      className="h-full"
      classNameImg="h-full w-full object-cover"
      imageUrl={`https://ns.clubmed.com/it/2022/UX/map-${isDesktop ? 'desktop' : 'mobile'}.png`}
    />
  );
  return (
    <ErrorBoundary>
      {!showLayer ? (
        <div className="relative isolate mb-30 h-200 overflow-hidden rounded-12 md:h-[352px]">
          {mapBackgroundImage}
          <div className="absolute inset-0 flex items-center justify-center">
            {onlyUniqResortProductList?.length === 0 && isLoading ? (
              <div className="inset-0 z-4 flex h-full w-full flex-col items-center justify-center bg-white/60">
                <Spinner iconName={ICONS.TRIDENT1} />
              </div>
            ) : (
              <Button
                className="w-auto"
                data-testid="worldMapButton"
                onClick={() => {
                  handleFullScreen(true);
                  track({
                    labelGA: 'openFullScreen',
                  });
                }}
                size="medium"
              >
                {t('faceting.worldMap.mapButton')}
              </Button>
            )}
          </div>
        </div>
      ) : (
        <Layer
          onClose={() => handleFullScreen(false)}
          header={
            <span className="sm:text-initial mx-auto block text-center font-bold">
              {t('faceting.worldMap.layerTitle')}
            </span>
          }
        >
          <div className="relative h-full overflow-hidden">
            {onlyUniqResortProductList?.length === 0 && isLoading && (
              <div className="fixed inset-0 z-4 flex flex-col items-center justify-center bg-white/60">
                <Spinner iconName={ICONS.TRIDENT1} />
              </div>
            )}
            {mapComponent}
            <WorldMapFilterList
              clearAllFacets={() => {
                dispatch(clearAll());
                setDefaultMapValue();
              }}
              clearFacet={(categoryId) => {
                dispatch(clearFacets(categoryId));
              }}
              closeProductCard={() => setPopupInfo(null)}
              isWorldMapFullScreen={showLayer}
              setDefaultMapValue={setDefaultMapValue}
            />
          </div>
        </Layer>
      )}
    </ErrorBoundary>
  );
};
